import { Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store, select } from '@ngrx/store';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { PageChangeEvent, RowClassArgs } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { BehaviorSubject, catchError, combineLatest, filter, map, Observable, of, startWith, Subject, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { IFilter, IGridState, PageSize } from 'src/app/core/models/base';
import { endOfLocalDay, sanitizeToDate, startOfLocalDay } from 'src/app/shared/helpers/formatDates';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { companySelector } from 'src/app/stores/global/companies/company-selectors';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';
import * as CompanyActions from "../../stores/global/companies/company-actions";
import { companiesWithInventory } from '../helpers/companies-with-inventory';
import { ISearchInventoryDevicesRequest } from '../models/inventory-device';
import { InventoryService } from '../services/inventory.service';

/** Options of the "next service" filter dropdown. Range shows the from/to date inputs. */
export enum NextServiceFilter {
  Overdue = 'overdue',
  Today = 'today',
  WithoutDate = 'withoutDate',
  Range = 'range'
}

/**
 * Inventory overview. Used for the HTEC view (route data `adminView: true`, all customers, customer filter)
 * and for the customer view (own inventory only).
 */
@UntilDestroy()
@Component({
    selector: 'app-inventory-view',
    templateUrl: './inventory-view.component.html',
    styleUrl: './inventory-view.component.scss',
    standalone: false
})
export class InventoryViewComponent implements OnInit {

  ////////////////
  // variables //
  //////////////

  isAdminView = false;
  vm$: Observable<any>;
  companyId: string = null;
  loading = false;

  public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: "contains",
  };

  activeOptions: Array<{ id: boolean, name: string }> = [];
  nextServiceOptions: Array<{ id: NextServiceFilter, name: string }> = [];
  nextServiceFilters = NextServiceFilter;

  // matches the backend default ordering (next service ascending) when no orderBy is sent
  sort: SortDescriptor[] = [{ field: 'nextService', dir: 'asc' }];

  public rowClass = (context: RowClassArgs) => {
    const nextService = context.dataItem?.nextService;
    return {
      'service-overdue': this.isOverdue(nextService),
      'service-today': this.isServiceToday(nextService)
    };
  };

  // Create filter form
  filterForm = this.fb.group({
    keyword: new FormControl(null),
    companyId: new UntypedFormControl(null),
    active: new UntypedFormControl(null),
    nextServiceFilter: new UntypedFormControl(null),
    nextServiceFrom: new UntypedFormControl(null),
    nextServiceTo: new UntypedFormControl(null)
  });

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store<AppStateInterface>,
    private route: ActivatedRoute,
    private router: Router,
    private inventoryService: InventoryService
  ) {
    this.isAdminView = this.route.snapshot.data['adminView'] === true;

    this.vm$ = combineLatest([
      this.gridState$,
      this.store.pipe(select(currentEmployeeSelector)).pipe(tap((employee) => {
        // customers only ever see their own company; HTEC filters by company optionally
        if (!this.isAdminView && employee?.company?.id) {
          this.companyId = employee.company.id;
          this.searchRequest$.next({
            ...this.searchRequest$.value,
            companyId: this.companyId,
          });
        }
      })),
      this.searchRequest$.pipe(
        filter(() => this.isAdminView || this.companyId != null),
        switchMap((request) => {
          this.loading = true;
          const updatedRequest = this.isAdminView ? request : { ...request, companyId: this.companyId };

          return this.inventoryService.searchInventoryDevices(updatedRequest)
            .pipe(
              catchError(() => of({ data: [], totalCount: 0 }))
            );
        }),
        tap(() => {
          this.loading = false;
        }),
        map(x => {
          return {
            data: x?.data ?? [],
            total: x?.totalCount ?? 0
          };
        }),
        startWith({ data: [], total: 0 })
      ),
      this.searchRequest$.pipe(startWith(null)),
      this.pageChangeSub$.pipe(
        withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
        })),
        tap((x) => {
          const pageNumber = Math.round(
            (x.pageChangeEvent.skip + x.pageChangeEvent.take) /
              x.pageChangeEvent.take
          );

          this.gridState$.next({
            skip: x.pageChangeEvent.skip,
            pageSize: x.pageChangeEvent.take,
          });

          this.searchRequest$.next({
            ...x.searchRequest,
            pageNumber: pageNumber,
            pageSize: x.pageChangeEvent.take,
          });
        }),
        startWith({ skip: 0, take: 10 })
      ),
      // customer filter: only companies with the inventory extension enabled
      this.store.pipe(select(companySelector), map(companies => companiesWithInventory(companies))),
      this.route.queryParams.pipe(
        tap(params => this.patchFilterForm(params)),
        withLatestFrom(this.searchRequest$),
        map(([params, searchRequest]) => ({ params, searchRequest })),
        tap(data => {
          if (JSON.stringify(data.params) !== "{}") {
            this.onSubmit(data.searchRequest)
          }
        }),
        take(1)
      )
    ]).pipe(
      map(
        ([
          gridState,
          currentEmployee,
          inventoryDevices,
          searchRequest,
          pageChangeSub,
          companies,
          filterForm
        ]) => ({
          gridState,
          currentEmployee,
          inventoryDevices,
          searchRequest,
          pageChangeSub,
          companies,
          filterForm
        })
      )
    );
  }

  ngOnInit(): void {
    this.activeOptions = [
      { id: true, name: translate('inventory.activeYes') },
      { id: false, name: translate('inventory.activeNo') }
    ];

    this.nextServiceOptions = [
      { id: NextServiceFilter.Overdue, name: translate('inventory.nextServiceOverdue') },
      { id: NextServiceFilter.Today, name: translate('inventory.nextServiceToday') },
      { id: NextServiceFilter.WithoutDate, name: translate('inventory.nextServiceWithoutDate') },
      { id: NextServiceFilter.Range, name: translate('inventory.nextServiceRange') }
    ];

    // the current employee (with its company) is loaded by the AppComponent and guaranteed by the AuthGuard for customer routes
    if (this.isAdminView) {
      this.store.dispatch(CompanyActions.getCompanies());
    }
  }

  ///////////////
  // Getters  //
  /////////////

  get pageSizes() {
    return PageSize;
  }

  ////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<ISearchInventoryDevicesRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small
  });

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();

  ////////////////
  // functions //
  //////////////

  formatDate(date: string): string {
    return date ? sanitizeToDate(date) : '-';
  }

  isOverdue(date: string): boolean {
    const day = this.toStartOfDay(date);
    return day != null && day.getTime() < this.toStartOfDay(new Date()).getTime();
  }

  isServiceToday(date: string): boolean {
    const day = this.toStartOfDay(date);
    return day != null && day.getTime() === this.toStartOfDay(new Date()).getTime();
  }

  private toStartOfDay(date: string | Date): Date | null {
    if (!date) {
      return null;
    }

    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    return day;
  }

  sortChange(sort: SortDescriptor[], searchRequest: ISearchInventoryDevicesRequest): void {
    this.sort = sort;
    this.gridState$.next({ pageSize: searchRequest.pageSize, skip: 0 });
    this.searchRequest$.next({
      ...searchRequest,
      pageNumber: 0,
      orderBy: sort.filter(x => x.dir).map(x => `${x.field} ${x.dir}`)
    });
  }

  patchFilterForm(params: any) {
    this.filterForm.patchValue({
      keyword: params['keyword'] || null,
      companyId: params['companyId'] || null,
      active: params['active'] === 'true' ? true : params['active'] === 'false' ? false : null,
      nextServiceFilter: Object.values(NextServiceFilter).includes(params['nextServiceFilter']) ? params['nextServiceFilter'] : null,
      nextServiceFrom: params['nextServiceFrom'] != null ? new Date(params['nextServiceFrom']) : null,
      nextServiceTo: params['nextServiceTo'] != null ? new Date(params['nextServiceTo']) : null
    });
  }

  onSubmit(request: ISearchInventoryDevicesRequest): void {
    const formValues = this.filterForm.value;

    const searchRequest: ISearchInventoryDevicesRequest = {
      ...request,
      pageNumber: 0,
      advancedFilter: undefined,
      companyId: this.isAdminView ? (formValues.companyId || undefined) : this.companyId,
      active: formValues.active === true || formValues.active === false ? formValues.active : undefined,
      nextServiceFrom: undefined,
      nextServiceTo: undefined,
      withoutNextService: undefined
    };

    // Trim the keyword before using
    const keyword = formValues.keyword ? formValues.keyword.trim() : null;

    // Fields to search for the keyword
    const searchFields = ['serialNumber', 'name', 'manufacturer', 'location'];

    if (keyword) {
      const keywordFilters: IFilter[] = searchFields.map(field => ({ field, operator: 'contains', value: keyword }));
      searchRequest.advancedFilter = { logic: 'or', filters: keywordFilters };
    }

    // Next service filter: overdue / today are expressed as date ranges, so the backend needs nothing new
    const today = new Date();

    switch (formValues.nextServiceFilter) {
      case NextServiceFilter.Overdue: {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        searchRequest.nextServiceTo = endOfLocalDay(yesterday);
        break;
      }
      case NextServiceFilter.Today:
        searchRequest.nextServiceFrom = startOfLocalDay(today);
        searchRequest.nextServiceTo = endOfLocalDay(today);
        break;
      case NextServiceFilter.WithoutDate:
        searchRequest.withoutNextService = true;
        break;
      case NextServiceFilter.Range:
        if (formValues.nextServiceFrom) {
          searchRequest.nextServiceFrom = startOfLocalDay(new Date(formValues.nextServiceFrom));
        }
        if (formValues.nextServiceTo) {
          searchRequest.nextServiceTo = endOfLocalDay(new Date(formValues.nextServiceTo));
        }
        break;
    }

    this.gridState$.next({ pageSize: request.pageSize, skip: 0 });
    this.searchRequest$.next(searchRequest);

    this.updateUrlWithQuery(formValues);
  }

  updateUrlWithQuery(formValues) {
    const isRange = formValues.nextServiceFilter === NextServiceFilter.Range;

    // Update URL with the filter parameters
    const queryParams = {
      keyword: formValues.keyword || null,
      companyId: formValues.companyId || null,
      active: formValues.active === true || formValues.active === false ? formValues.active : null,
      nextServiceFilter: formValues.nextServiceFilter || null,
      nextServiceFrom: isRange && formValues.nextServiceFrom ? new Date(formValues.nextServiceFrom).toISOString() : null,
      nextServiceTo: isRange && formValues.nextServiceTo ? new Date(formValues.nextServiceTo).toISOString() : null
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  resetFilter(): void {
    // reset form
    this.filterForm.reset();
    this.sort = [{ field: 'nextService', dir: 'asc' }];

    this.searchRequest$.next({ pageNumber: 0, pageSize: this.pageSizes.Small, companyId: this.isAdminView ? undefined : this.companyId })
    this.gridState$.next({ pageSize: PageSize.Small, skip: 0 })

    // reset url params
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      queryParamsHandling: '',
    });
  }
}
